/**
 * Stock Control — Leakage prevention cockpit.
 *
 * Five lenses:
 *   • Overview     — KPI strip + alerts
 *   • Reservations — what's locked, by job, with shorts
 *   • Field Buys   — retail purchase log + bookkeeper review queue
 *   • Cycle Counts — ABC cadence: who's due, what's overdue
 *   • Leakage      — $ shrinkage YTD, top leaking SKUs, reorder suggestions
 *
 * Design follows the cockpit standard (Lead Performance reference):
 *   typographic header, hairline KPI strip, underline tabs, neutral chrome.
 *   Color reserved for SLA signals only (overdue counts, shorts, lost stock).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PermissionGate } from "@/components/PermissionGate";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { CockpitTabs, CockpitKpiStrip } from "@/components/cockpit/CockpitShell";
import { useStockLeakage } from "@/data/stockLeakage/StockLeakageContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AbcClass, FieldPurchaseReason, VarianceReason } from "@/data/stockLeakage/types";
import { COUNT_CADENCE_DAYS } from "@/data/stockLeakage/types";
import { formatDistanceToNowStrict } from "date-fns";

type Tab = "overview" | "reservations" | "field-buys" | "counts" | "leakage";
const TABS: readonly Tab[] = ["overview", "reservations", "field-buys", "counts", "leakage"];
const isTab = (v: string | null): v is Tab => !!v && (TABS as readonly string[]).includes(v);

const FIELD_REASONS: { value: FieldPurchaseReason; label: string }[] = [
  { value: "stock_out", label: "Stock-out — should have had it" },
  { value: "damaged_on_site", label: "Damaged on site" },
  { value: "design_change", label: "Design / scope change" },
  { value: "miscounted_in_kit", label: "Miscount in staged kit" },
  { value: "specialty_item", label: "Specialty — never stocked" },
  { value: "other", label: "Other" },
];

const VARIANCE_REASONS: { value: VarianceReason; label: string }[] = [
  { value: "cycle_count", label: "Routine count" },
  { value: "lost", label: "Lost — unexplained" },
  { value: "damaged", label: "Damaged in warehouse" },
  { value: "miscount_at_receive", label: "Miscount at receive" },
  { value: "transfer_out", label: "Transferred out" },
  { value: "adjustment", label: "Bookkeeper adjustment" },
];

export default function StockControl() {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { stockRecords } = useWarehouseInventory();
  const {
    reservations, fieldPurchases, cycleCounts, varianceEvents, policies,
    countDueRows, reorderSuggestions, shrinkageByYear, fieldPurchaseRate, topLeakingSkus,
    reviewFieldPurchase, recordCycleCount, releaseReservation, pickReservation,
  } = useStockLeakage();

  // Pricing visibility per memory: warehouse/field roles see qty only.
  const canSeeDollars = useMemo(() => {
    return hasPerm("accounting_view");
  }, [hasPerm]);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab: Tab = isTab(searchParams.get("tab")) ? (searchParams.get("tab") as Tab) : "overview";
  const [tab, setTab] = useState<Tab>(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get("tab") !== tab) {
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  /* ── Headline metrics ── */
  const activeReservations = reservations.filter(r => r.status === "reserved").length;
  const shortedReservations = reservations.filter(r => r.status === "shorted").length;
  const pendingReviews = fieldPurchases.filter(f => f.reviewStatus === "pending").length;
  const overdueCounts = countDueRows.filter(r => r.isOverdue).length;

  const kpiItems = [
    { label: "Active reservations", value: activeReservations },
    { label: "Shorted", value: shortedReservations, tone: shortedReservations > 0 ? "bad" as const : "default" as const },
    { label: "Field-buy review", value: pendingReviews, tone: pendingReviews > 0 ? "warn" as const : "default" as const },
    { label: "Counts overdue", value: overdueCounts, tone: overdueCounts > 0 ? "warn" as const : "default" as const },
    {
      label: canSeeDollars ? "Shrinkage YTD" : "Variance events",
      value: canSeeDollars ? `$${shrinkageByYear.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : varianceEvents.length,
    },
  ];

  const tabDefs = [
    { value: "overview" as const,     label: "Overview" },
    { value: "reservations" as const, label: "Reservations", count: activeReservations || undefined },
    { value: "field-buys" as const,   label: "Field Purchases", count: pendingReviews || undefined },
    { value: "counts" as const,       label: "Cycle Counts", count: overdueCounts || undefined },
    { value: "leakage" as const,      label: "Leakage Report" },
  ];

  return (
    <CockpitPage
      title="Stock Control"
      subtitle="Reservations, field-purchase log, cycle counts, and shrinkage — the discipline layer over warehouse stock."
      controls={
        <span className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
          {policies.length} stock SKUs · ABC tiered
        </span>
      }
      belowHeader={<CockpitKpiStrip items={kpiItems} />}
      tabs={
        <CockpitTabs
          tabs={tabDefs.map(t => ({ value: t.value, label: t.label, count: t.count }))}
          value={tab}
          onChange={(v) => setTab(v as Tab)}
        />
      }
    >
      <div className="mt-5 space-y-5">
        {tab === "overview" && (
          <OverviewLens
            reorderSuggestions={reorderSuggestions}
            topLeakingSkus={topLeakingSkus}
            fieldPurchaseRate={fieldPurchaseRate}
            shortedReservations={shortedReservations}
            canSeeDollars={canSeeDollars}
          />
        )}

        {tab === "reservations" && (
          <ReservationsLens
            reservations={reservations}
            onRelease={releaseReservation}
            onPick={pickReservation}
          />
        )}

        {tab === "field-buys" && (
          <FieldPurchasesLens
            purchases={fieldPurchases}
            canSeeDollars={canSeeDollars}
            onReview={reviewFieldPurchase}
          />
        )}

        {tab === "counts" && (
          <CountsLens
            countDueRows={countDueRows}
            cycleCounts={cycleCounts}
            stockRecords={stockRecords}
            onRecordCount={recordCycleCount}
          />
        )}

        {tab === "leakage" && (
          <LeakageLens
            varianceEvents={varianceEvents}
            topLeakingSkus={topLeakingSkus}
            shrinkageByYear={shrinkageByYear}
            canSeeDollars={canSeeDollars}
          />
        )}
      </div>
    </CockpitPage>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Section primitives — match Warehouse Hub for consistency             */
/* ═══════════════════════════════════════════════════════════════════ */

function Section({ title, count, hint, children }: {
  title: string; count?: number; hint?: string; children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-medium tracking-tight text-foreground">
          {title}
          {typeof count === "number" && (
            <span className="ml-2 text-muted-foreground/70 tabular-nums font-normal">{count}</span>
          )}
        </h2>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const TH = "text-left py-2 px-3 font-medium text-muted-foreground/80 uppercase text-[10px] tracking-[0.08em]";

function EmptyRow({ msg }: { msg: string }) {
  return <p className="py-8 text-center text-xs text-muted-foreground">{msg}</p>;
}

function AbcChip({ cls }: { cls: AbcClass }) {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-medium tabular-nums border border-border/60 text-muted-foreground bg-muted/30">
      {cls}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Lens 1 — Overview                                                    */
/* ═══════════════════════════════════════════════════════════════════ */

function OverviewLens({
  reorderSuggestions, topLeakingSkus, fieldPurchaseRate, shortedReservations, canSeeDollars,
}: {
  reorderSuggestions: ReturnType<typeof useStockLeakage>["reorderSuggestions"];
  topLeakingSkus: ReturnType<typeof useStockLeakage>["topLeakingSkus"];
  fieldPurchaseRate: ReturnType<typeof useStockLeakage>["fieldPurchaseRate"];
  shortedReservations: number;
  canSeeDollars: boolean;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Health signals */}
      <Section title="Health signals" hint="The four numbers a CFO watches weekly.">
        <div className="border-y border-border/60 divide-y divide-border/60">
          <SignalRow
            label="Field-purchase rate · 90 days"
            value={`${fieldPurchaseRate.pct}%`}
            sub={`${fieldPurchaseRate.jobsWithPurchase} of ${fieldPurchaseRate.totalActiveJobs} jobs needed a retail buy`}
            tone={fieldPurchaseRate.pct > 25 ? "warn" : fieldPurchaseRate.pct > 50 ? "bad" : "default"}
          />
          <SignalRow
            label="Reservation breaches"
            value={shortedReservations}
            sub="Jobs where stock ran out before pick"
            tone={shortedReservations > 0 ? "bad" : "default"}
          />
          <SignalRow
            label="Reorder suggestions"
            value={reorderSuggestions.length}
            sub="SKUs at or below min-stock threshold"
            tone={reorderSuggestions.length > 5 ? "warn" : "default"}
          />
          <SignalRow
            label={canSeeDollars ? "Top leaking SKU (cost)" : "Top leaking SKU"}
            value={topLeakingSkus[0]?.sku ?? "—"}
            sub={
              topLeakingSkus[0]
                ? canSeeDollars
                  ? `$${topLeakingSkus[0].costImpact.toFixed(0)} · ${Math.abs(topLeakingSkus[0].netDelta)} units`
                  : `${Math.abs(topLeakingSkus[0].netDelta)} units down`
                : "No leakage detected"
            }
            tone={topLeakingSkus[0] ? "warn" : "default"}
          />
        </div>
      </Section>

      {/* Reorder suggestions */}
      <Section title="Reorder suggestions" count={reorderSuggestions.length} hint="At or below min — propose a stock PO.">
        {reorderSuggestions.length === 0 ? (
          <EmptyRow msg="All stock above minimum thresholds." />
        ) : (
          <div className="overflow-auto border-y border-border/60">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60">
                  <th className={TH}>SKU</th>
                  <th className={TH}>Available</th>
                  <th className={TH}>Min</th>
                  <th className={TH}>Suggest order</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {reorderSuggestions.map(r => (
                  <tr key={r.sku} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">{r.sku}</td>
                    <td className="py-2 px-3 tabular-nums text-destructive">{r.available}</td>
                    <td className="py-2 px-3 tabular-nums text-muted-foreground">{r.min}</td>
                    <td className="py-2 px-3 tabular-nums text-foreground">+{r.suggestQty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function SignalRow({ label, value, sub, tone = "default" }: {
  label: string; value: string | number; sub?: string;
  tone?: "default" | "warn" | "bad";
}) {
  const toneCls =
    tone === "bad" ? "text-destructive" :
    tone === "warn" ? "text-warning" : "text-foreground";
  return (
    <div className="flex items-baseline justify-between gap-4 py-3 px-4">
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        {sub && <p className="text-[11px] text-muted-foreground/70 mt-0.5">{sub}</p>}
      </div>
      <p className={cn("text-lg font-semibold tabular-nums tracking-tight shrink-0", toneCls)}>{value}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Lens 2 — Reservations                                                */
/* ═══════════════════════════════════════════════════════════════════ */

function ReservationsLens({
  reservations, onRelease, onPick,
}: {
  reservations: ReturnType<typeof useStockLeakage>["reservations"];
  onRelease: (id: string, reason: string) => void;
  onPick: (id: string, pickedBy: string) => void;
}) {
  const [filter, setFilter] = useState<"all" | "reserved" | "shorted" | "picked" | "released">("all");
  const filtered = reservations.filter(r => filter === "all" ? true : r.status === filter);

  return (
    <Section
      title="Stock reservations"
      count={filtered.length}
      hint={`Stock locks at ~7 days pre-install. ${COUNT_CADENCE_DAYS.A}/30/90-day count cadence governs availability accuracy.`}
    >
      {/* Filter pills */}
      <div className="inline-flex items-center gap-0.5 rounded-md border border-border/60 bg-muted/30 p-0.5">
        {(["all", "reserved", "shorted", "picked", "released"] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-2.5 py-1 rounded text-[11px] capitalize transition-colors",
              filter === f
                ? "bg-primary/15 text-primary shadow-sm ring-1 ring-primary/30 font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium",
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyRow msg="No reservations match this filter." />
      ) : (
        <div className="overflow-auto border-y border-border/60">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                <th className={TH}>Job</th>
                <th className={TH}>SKU</th>
                <th className={TH}>Qty</th>
                <th className={TH}>Status</th>
                <th className={TH}>Reserved</th>
                <th className={TH}>By</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-muted/30 transition-colors">
                  <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">{r.jobId}</td>
                  <td className="py-2 px-3">
                    <div className="font-mono text-[11px] text-foreground/90">{r.sku}</div>
                    <div className="text-[11px] text-muted-foreground">{r.productName}</div>
                  </td>
                  <td className="py-2 px-3 tabular-nums text-foreground">{r.quantity}</td>
                  <td className="py-2 px-3">
                    <ReservationStatusPill status={r.status} />
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    {formatDistanceToNowStrict(new Date(r.reservedAt), { addSuffix: true })}
                  </td>
                  <td className="py-2 px-3 text-foreground/80">{r.reservedBy}</td>
                  <td className="py-2 px-3 text-right">
                    {r.status === "reserved" && (
                      <div className="flex items-center justify-end gap-1">
                        <PermissionGate permission="warehouse_edit">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                            onClick={() => onPick(r.id, "Operator")}>
                            Mark picked
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="warehouse_edit">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-muted-foreground"
                            onClick={() => onRelease(r.id, "Manual release")}>
                            Release
                          </Button>
                        </PermissionGate>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function ReservationStatusPill({ status }: { status: string }) {
  const cls =
    status === "shorted" ? "text-destructive border-destructive/30 bg-destructive/5" :
    status === "reserved" ? "text-foreground border-border/60 bg-muted/30" :
    status === "picked" ? "text-success border-success/30 bg-success/5" :
    "text-muted-foreground border-border/60 bg-muted/20";
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border capitalize", cls)}>
      {status}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Lens 3 — Field Purchases                                             */
/* ═══════════════════════════════════════════════════════════════════ */

function FieldPurchasesLens({
  purchases, canSeeDollars, onReview,
}: {
  purchases: ReturnType<typeof useStockLeakage>["fieldPurchases"];
  canSeeDollars: boolean;
  onReview: (id: string, decision: "approved" | "flagged", reviewer: string, note?: string) => void;
}) {
  const [logOpen, setLogOpen] = useState(false);

  return (
    <Section
      title="Field purchase log"
      count={purchases.length}
      hint="Soft gate — installer logs the buy with a reason. Bookkeeper reviews. Drives the leakage scorecard."
    >
      <div className="flex justify-end">
        <PermissionGate permission="inventory_create">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setLogOpen(true)}>
            Log a field purchase
          </Button>
        </PermissionGate>
      </div>

      {purchases.length === 0 ? (
        <EmptyRow msg="No field purchases logged yet." />
      ) : (
        <div className="overflow-auto border-y border-border/60">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                <th className={TH}>When</th>
                <th className={TH}>Job</th>
                <th className={TH}>By</th>
                <th className={TH}>Vendor</th>
                <th className={TH}>Items</th>
                <th className={TH}>Reason</th>
                {canSeeDollars && <th className={cn(TH, "text-right")}>Amount</th>}
                <th className={TH}>Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {purchases.map(f => (
                <tr key={f.id} className="hover:bg-muted/30 transition-colors align-top">
                  <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNowStrict(new Date(f.purchasedAt), { addSuffix: true })}
                  </td>
                  <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">{f.jobId}</td>
                  <td className="py-2 px-3 text-foreground/80">{f.purchasedBy}</td>
                  <td className="py-2 px-3 text-foreground/80">{f.vendor}</td>
                  <td className="py-2 px-3 text-muted-foreground max-w-[220px]">
                    {f.items.map((it, i) => (
                      <div key={i} className="truncate">
                        <span className="text-foreground/80">{it.quantity}×</span> {it.description}
                      </div>
                    ))}
                  </td>
                  <td className="py-2 px-3">
                    <ReasonChip reason={f.reason} />
                    {f.notes && <p className="text-[10px] text-muted-foreground mt-0.5 max-w-[180px] truncate">{f.notes}</p>}
                  </td>
                  {canSeeDollars && (
                    <td className="py-2 px-3 text-right tabular-nums text-foreground">
                      ${f.amount.toFixed(2)}
                    </td>
                  )}
                  <td className="py-2 px-3">
                    <ReviewStatusPill status={f.reviewStatus} />
                  </td>
                  <td className="py-2 px-3 text-right">
                    {f.reviewStatus === "pending" && (
                      <div className="flex items-center justify-end gap-1">
                        <PermissionGate permission="inventory_edit">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                            onClick={() => onReview(f.id, "approved", "Bookkeeper")}>
                            Approve
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="inventory_edit">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-destructive"
                            onClick={() => onReview(f.id, "flagged", "Bookkeeper", "Needs investigation")}>
                            Flag
                          </Button>
                        </PermissionGate>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FieldPurchaseDialog open={logOpen} onOpenChange={setLogOpen} />
    </Section>
  );
}

function ReasonChip({ reason }: { reason: FieldPurchaseReason }) {
  const tone =
    reason === "stock_out" ? "text-destructive border-destructive/30 bg-destructive/5" :
    reason === "design_change" ? "text-foreground border-border/60 bg-muted/30" :
    reason === "specialty_item" ? "text-muted-foreground border-border/60 bg-muted/20" :
    "text-warning border-warning/30 bg-warning/5";
  const label = FIELD_REASONS.find(r => r.value === reason)?.label.split(" — ")[0] ?? reason;
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border", tone)}>
      {label}
    </span>
  );
}

function ReviewStatusPill({ status }: { status: "pending" | "approved" | "flagged" }) {
  const cls =
    status === "flagged" ? "text-destructive border-destructive/30 bg-destructive/5" :
    status === "approved" ? "text-success border-success/30 bg-success/5" :
    "text-muted-foreground border-border/60 bg-muted/30";
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border capitalize", cls)}>
      {status}
    </span>
  );
}

function FieldPurchaseDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { logFieldPurchase } = useStockLeakage();
  const [jobId, setJobId] = useState("");
  const [vendor, setVendor] = useState("Home Depot");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [reason, setReason] = useState<FieldPurchaseReason>("stock_out");
  const [notes, setNotes] = useState("");

  const reset = () => {
    setJobId(""); setVendor("Home Depot"); setAmount(""); setDescription("");
    setQuantity("1"); setReason("stock_out"); setNotes("");
  };

  const submit = () => {
    if (!jobId || !description || !amount) return;
    logFieldPurchase({
      jobId,
      jobLabel: jobId,
      purchasedBy: "Field crew",
      vendor,
      amount: parseFloat(amount),
      items: [{ description, quantity: parseInt(quantity, 10) || 1 }],
      reason,
      notes: notes || undefined,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log a field purchase</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Job ID</Label>
            <Input value={jobId} onChange={(e) => setJobId(e.target.value)} placeholder="JOB-001" className="h-8 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Vendor</Label>
              <Input value={vendor} onChange={(e) => setVendor(e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-[1fr_80px] gap-3">
            <div>
              <Label className="text-xs">Item description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Stainless screws #10 x 1.5&quot;" className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Qty</Label>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as FieldPurchaseReason)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value} className="text-sm">{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm min-h-[60px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={!jobId || !description || !amount}>
            Log purchase
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Lens 4 — Cycle Counts                                                */
/* ═══════════════════════════════════════════════════════════════════ */

function CountsLens({
  countDueRows, cycleCounts, stockRecords, onRecordCount,
}: {
  countDueRows: ReturnType<typeof useStockLeakage>["countDueRows"];
  cycleCounts: ReturnType<typeof useStockLeakage>["cycleCounts"];
  stockRecords: ReturnType<typeof useWarehouseInventory>["stockRecords"];
  onRecordCount: ReturnType<typeof useStockLeakage>["recordCycleCount"];
}) {
  const [countOpen, setCountOpen] = useState(false);
  const [countingSku, setCountingSku] = useState<string | null>(null);

  const startCount = (sku: string) => {
    setCountingSku(sku);
    setCountOpen(true);
  };

  return (
    <div className="space-y-6">
      <Section title="Counts due" count={countDueRows.filter(r => r.daysUntilDue <= 3).length}
        hint="ABC cadence — A weekly · B monthly · C quarterly. Overdue rows flag in red.">
        <div className="overflow-auto border-y border-border/60">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                <th className={TH}>SKU</th>
                <th className={TH}>Class</th>
                <th className={TH}>Cadence</th>
                <th className={TH}>Last counted</th>
                <th className={TH}>Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {countDueRows.map(r => (
                <tr key={r.sku} className={cn("hover:bg-muted/30 transition-colors", r.isOverdue && "bg-destructive/5")}>
                  <td className="py-2 px-3">
                    <div className="font-mono text-[11px] text-foreground/90">{r.sku}</div>
                    <div className="text-[11px] text-muted-foreground">{r.productName}</div>
                  </td>
                  <td className="py-2 px-3"><AbcChip cls={r.abcClass} /></td>
                  <td className="py-2 px-3 text-muted-foreground tabular-nums">
                    every {COUNT_CADENCE_DAYS[r.abcClass]}d
                  </td>
                  <td className="py-2 px-3 text-muted-foreground tabular-nums">
                    {r.daysSinceLastCount === null ? "Never" : `${r.daysSinceLastCount}d ago`}
                  </td>
                  <td className="py-2 px-3">
                    {r.isOverdue ? (
                      <span className="text-destructive text-[11px] font-medium tabular-nums">
                        {Math.abs(r.daysUntilDue)}d overdue
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-[11px] tabular-nums">
                        due in {r.daysUntilDue}d
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <PermissionGate permission="warehouse_edit">
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                        onClick={() => startCount(r.sku)}>
                        Count
                      </Button>
                    </PermissionGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Recent counts" count={cycleCounts.length}>
        {cycleCounts.length === 0 ? (
          <EmptyRow msg="No counts recorded yet." />
        ) : (
          <div className="overflow-auto border-y border-border/60">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60">
                  <th className={TH}>When</th>
                  <th className={TH}>SKU</th>
                  <th className={TH}>System</th>
                  <th className={TH}>Counted</th>
                  <th className={TH}>Variance</th>
                  <th className={TH}>Reason</th>
                  <th className={TH}>By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {cycleCounts.map(c => (
                  <tr key={c.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNowStrict(new Date(c.countedAt), { addSuffix: true })}
                    </td>
                    <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">{c.sku}</td>
                    <td className="py-2 px-3 tabular-nums text-muted-foreground">{c.systemQuantity}</td>
                    <td className="py-2 px-3 tabular-nums text-foreground">{c.countedQuantity}</td>
                    <td className={cn(
                      "py-2 px-3 tabular-nums font-medium",
                      c.variance < 0 ? "text-destructive" : c.variance > 0 ? "text-warning" : "text-muted-foreground",
                    )}>
                      {c.variance > 0 ? `+${c.variance}` : c.variance}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {c.varianceReason ? (VARIANCE_REASONS.find(r => r.value === c.varianceReason)?.label ?? c.varianceReason) : "—"}
                    </td>
                    <td className="py-2 px-3 text-foreground/80">{c.countedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <CycleCountDialog
        open={countOpen}
        onOpenChange={(v) => { setCountOpen(v); if (!v) setCountingSku(null); }}
        sku={countingSku}
        stockRecords={stockRecords}
        countDueRows={countDueRows}
        onSubmit={onRecordCount}
      />
    </div>
  );
}

function CycleCountDialog({
  open, onOpenChange, sku, stockRecords, countDueRows, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sku: string | null;
  stockRecords: ReturnType<typeof useWarehouseInventory>["stockRecords"];
  countDueRows: ReturnType<typeof useStockLeakage>["countDueRows"];
  onSubmit: ReturnType<typeof useStockLeakage>["recordCycleCount"];
}) {
  const stock = sku ? stockRecords.find(s => s.sku === sku) : undefined;
  const dueRow = sku ? countDueRows.find(r => r.sku === sku) : undefined;
  const systemQty = stock?.currentStock ?? 0;

  const [counted, setCounted] = useState("");
  const [reason, setReason] = useState<VarianceReason>("cycle_count");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setCounted(String(systemQty));
      setReason("cycle_count");
      setNote("");
    }
  }, [open, systemQty]);

  if (!sku || !stock || !dueRow) return null;

  const countedNum = parseInt(counted, 10) || 0;
  const variance = countedNum - systemQty;

  const submit = () => {
    onSubmit({
      sku,
      productName: stock.productName,
      abcClass: dueRow.abcClass,
      systemQuantity: systemQty,
      countedQuantity: countedNum,
      countedBy: "Operator",
      varianceReason: variance !== 0 ? reason : undefined,
      varianceNote: variance !== 0 ? note : undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cycle count · {sku}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border/60 rounded p-3 bg-muted/20">
            <p className="text-[11px] text-muted-foreground">{stock.productName}</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Class <span className="text-foreground font-medium">{dueRow.abcClass}</span>
              {" · "}System says <span className="text-foreground font-medium tabular-nums">{systemQty}</span>
            </p>
          </div>
          <div>
            <Label className="text-xs">Physical count on shelf</Label>
            <Input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} className="h-9 text-base tabular-nums" autoFocus />
          </div>
          {variance !== 0 && (
            <>
              <div className={cn(
                "rounded p-2 text-xs flex items-center justify-between",
                variance < 0 ? "bg-destructive/5 text-destructive border border-destructive/20"
                             : "bg-warning/5 text-warning border border-warning/20",
              )}>
                <span>Variance vs system</span>
                <span className="font-semibold tabular-nums">{variance > 0 ? `+${variance}` : variance}</span>
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Select value={reason} onValueChange={(v) => setReason(v as VarianceReason)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VARIANCE_REASONS.filter(r => r.value !== "cycle_count").map(r => (
                      <SelectItem key={r.value} value={r.value} className="text-sm">{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="text-sm min-h-[50px]"
                  placeholder="What happened?" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit}>Save count</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Lens 5 — Leakage Report                                              */
/* ═══════════════════════════════════════════════════════════════════ */

function LeakageLens({
  varianceEvents, topLeakingSkus, shrinkageByYear, canSeeDollars,
}: {
  varianceEvents: ReturnType<typeof useStockLeakage>["varianceEvents"];
  topLeakingSkus: ReturnType<typeof useStockLeakage>["topLeakingSkus"];
  shrinkageByYear: number;
  canSeeDollars: boolean;
}) {
  return (
    <div className="space-y-6">
      <Section
        title="Top leaking SKUs"
        count={topLeakingSkus.length}
        hint={canSeeDollars ? "Ranked by dollar impact this year." : "Ranked by units lost this year."}
      >
        {topLeakingSkus.length === 0 ? (
          <EmptyRow msg="No leakage detected." />
        ) : (
          <div className="overflow-auto border-y border-border/60">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60">
                  <th className={TH}>SKU</th>
                  <th className={TH}>Net delta (units)</th>
                  {canSeeDollars && <th className={cn(TH, "text-right")}>Cost impact</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {topLeakingSkus.map(r => (
                  <tr key={r.sku} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-3">
                      <div className="font-mono text-[11px] text-foreground/90">{r.sku}</div>
                      <div className="text-[11px] text-muted-foreground">{r.productName}</div>
                    </td>
                    <td className="py-2 px-3 tabular-nums text-destructive font-medium">{r.netDelta}</td>
                    {canSeeDollars && (
                      <td className="py-2 px-3 tabular-nums text-right text-destructive font-medium">
                        ${r.costImpact.toFixed(0)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Variance event log"
        count={varianceEvents.length}
        hint="Every inventory delta outside a normal job pick. Audit-grade."
      >
        {varianceEvents.length === 0 ? (
          <EmptyRow msg="No variance events recorded." />
        ) : (
          <div className="overflow-auto border-y border-border/60">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60">
                  <th className={TH}>When</th>
                  <th className={TH}>SKU</th>
                  <th className={TH}>Δ Qty</th>
                  <th className={TH}>Reason</th>
                  {canSeeDollars && <th className={cn(TH, "text-right")}>Cost</th>}
                  <th className={TH}>By</th>
                  <th className={TH}>Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {varianceEvents.map(e => (
                  <tr key={e.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNowStrict(new Date(e.recordedAt), { addSuffix: true })}
                    </td>
                    <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">{e.sku}</td>
                    <td className={cn(
                      "py-2 px-3 tabular-nums font-medium",
                      e.quantityDelta < 0 ? "text-destructive" : "text-warning",
                    )}>
                      {e.quantityDelta > 0 ? `+${e.quantityDelta}` : e.quantityDelta}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {VARIANCE_REASONS.find(r => r.value === e.reason)?.label ?? e.reason}
                    </td>
                    {canSeeDollars && (
                      <td className="py-2 px-3 tabular-nums text-right text-destructive">
                        {e.costImpact ? `$${e.costImpact.toFixed(0)}` : "—"}
                      </td>
                    )}
                    <td className="py-2 px-3 text-foreground/80">{e.recordedBy}</td>
                    <td className="py-2 px-3 text-muted-foreground max-w-[240px] truncate">{e.reasonNote || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {canSeeDollars && (
        <div className="border border-border/60 rounded p-4 bg-muted/20 flex items-baseline justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Shrinkage YTD</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Sum of negative-delta variance events recorded this year.
            </p>
          </div>
          <p className="text-2xl font-semibold tabular-nums text-foreground">
            ${shrinkageByYear.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
      )}
    </div>
  );
}
